import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bitcoin,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Landmark,
  FileCheck,
  Lock,
  FlaskConical,
  ChevronDown,
  AlertTriangle,
  CreditCard,
  Coins,
} from 'lucide-react';
import './LandingPage.css';

// Inline FAQ item (local to this page so it can be styled/tracked separately)
const PeptideFAQItem = ({ question, answer, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div
      data-testid={testId}
      onClick={() => setIsOpen(!isOpen)}
      style={{
        background: isOpen ? 'rgba(74, 222, 128, 0.05)' : 'rgba(255, 255, 255, 0.02)',
        border: `1px solid ${isOpen ? 'rgba(74, 222, 128, 0.3)' : 'rgba(255, 255, 255, 0.06)'}`,
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginBottom: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
        }}
      >
        <h4
          style={{
            color: isOpen ? '#4ade80' : '#e4e6eb',
            fontSize: '1.05rem',
            fontWeight: 500,
            margin: 0,
          }}
        >
          {question}
        </h4>
        <ChevronDown
          size={20}
          style={{
            color: isOpen ? '#4ade80' : '#6b7280',
            flexShrink: 0,
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </div>
      <div
        style={{
          maxHeight: isOpen ? '320px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        <p
          style={{
            padding: '0 24px 20px 24px',
            color: '#9ca3af',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {answer}
        </p>
      </div>
    </div>
  );
};

const PEPTIDE_PREFIX = '[Peptide Business Inquiry] ';

const PeptideLanding = () => {
  const navigate = useNavigate();

  // Fire-and-forget visit tracking for conversion analytics
  React.useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${BACKEND_URL}/api/analytics/track-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: 'peptide' }),
    }).catch(() => {});
  }, []);

  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    monthlyVolume: '',
    usdtWallet: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setInquiryForm({ ...inquiryForm, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    const composedMessage =
      PEPTIDE_PREFIX +
      `Business: ${inquiryForm.businessName || 'N/A'} | ` +
      `Expected monthly volume: ${inquiryForm.monthlyVolume || 'N/A'} | ` +
      `USDT wallet: ${inquiryForm.usdtWallet || 'not provided'}\n\n` +
      (inquiryForm.message || 'No additional details provided.');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: inquiryForm.name,
          email: inquiryForm.email,
          phone: inquiryForm.phone,
          message: composedMessage,
        }),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message:
            "Thank you! Our peptide-merchant specialist will reach out within 24 hours.",
        });
        setInquiryForm({
          name: '',
          email: '',
          phone: '',
          businessName: '',
          monthlyVolume: '',
          usdtWallet: '',
          message: '',
        });
      } else {
        setStatus({
          type: 'error',
          message: 'Failed to submit inquiry. Please try again.',
        });
      }
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'An error occurred. Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToInquiry = () => {
    const el = document.getElementById('peptide-inquiry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page" data-testid="peptide-landing-page">
      {/* Hero */}
      <section className="hero-section">
        <nav className="navbar" data-testid="peptide-navbar">
          <div className="nav-container">
            <div
              className="logo"
              data-testid="peptide-logo"
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
            >
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/auth')}
              data-testid="peptide-get-started-btn"
            >
              Get Started <ArrowRight size={18} />
            </button>
          </div>
        </nav>

        <div className="hero-content" data-testid="peptide-hero-content">
          <div
            className="hero-badge"
            style={{
              background: 'rgba(74, 222, 128, 0.12)',
              border: '1px solid rgba(74, 222, 128, 0.35)',
              color: '#4ade80',
            }}
          >
            <FlaskConical size={16} />
            <span>Built for Peptide & Research Chemical Merchants</span>
          </div>
          <h1 className="hero-title" data-testid="peptide-hero-title">
            The Payment Platform
            <br />
            <span className="gradient-text">Peptide Businesses Actually Get Approved For</span>
          </h1>
          <p className="hero-subtitle" data-testid="peptide-hero-subtitle">
            Stripe and Square shut you down the moment they see "peptide."
            <br />
            We don't. Accept <strong style={{ color: '#86efac' }}>credit & debit cards</strong> through
            our sponsor banks like <strong style={{ color: '#86efac' }}>Santander</strong> — and we
            settle your payouts directly to your <strong style={{ color: '#86efac' }}>USDT</strong> wallet.
          </p>

          <div
            className="hero-cta"
            style={{ marginTop: '32px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}
          >
            <button
              className="btn btn-primary btn-large"
              onClick={scrollToInquiry}
              data-testid="peptide-hero-cta-primary"
            >
              Apply for a Merchant Account <ArrowRight size={20} />
            </button>
            <button
              className="btn btn-secondary btn-large"
              onClick={() => {
                const el = document.getElementById('peptide-how');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              data-testid="peptide-hero-cta-secondary"
            >
              How It Works
            </button>
          </div>

          <div className="hero-stats" style={{ marginTop: '40px' }}>
            <div className="stat">
              <CreditCard size={24} className="stat-icon" />
              <div className="stat-label">Card Processing</div>
            </div>
            <div className="stat">
              <Landmark size={24} className="stat-icon" />
              <div className="stat-label">Sponsor Bank</div>
            </div>
            <div className="stat">
              <Coins size={24} className="stat-icon" />
              <div className="stat-label">USDT Payouts</div>
            </div>
            <div className="stat">
              <Shield size={24} className="stat-icon" />
              <div className="stat-label">KYC + AML</div>
            </div>
            <div className="stat">
              <FileCheck size={24} className="stat-icon" />
              <div className="stat-label">Compliance-First</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section
        className="features-section"
        data-testid="peptide-problem-section"
        style={{ paddingTop: '40px' }}
      >
        <div className="container">
          <h2 className="section-title">Why Traditional Processors Fail Peptide Merchants</h2>
          <p className="section-subtitle">
            And why our sponsor-bank model was built specifically to solve this.
          </p>

          <div
            className="features-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '24px',
              marginTop: '40px',
            }}
          >
            <div
              className="feature-card"
              data-testid="peptide-pain-card-1"
              style={{ borderTop: '3px solid #ef4444' }}
            >
              <div className="feature-icon" style={{ color: '#ef4444' }}>
                <AlertTriangle size={32} />
              </div>
              <h3 className="feature-title">Sudden Account Freezes</h3>
              <p className="feature-description">
                Generic processors flag peptide SKUs as "high-risk" and freeze funds — sometimes
                holding six-figure balances for 180 days.
              </p>
            </div>

            <div
              className="feature-card"
              data-testid="peptide-pain-card-2"
              style={{ borderTop: '3px solid #ef4444' }}
            >
              <div className="feature-icon" style={{ color: '#ef4444' }}>
                <AlertTriangle size={32} />
              </div>
              <h3 className="feature-title">MATCH List Nightmares</h3>
              <p className="feature-description">
                One TOS violation and you're on the MATCH list for 5 years — blacklisted from
                virtually every processor in North America.
              </p>
            </div>

            <div
              className="feature-card"
              data-testid="peptide-solution-card"
              style={{
                borderTop: '3px solid #4ade80',
                background: 'rgba(74, 222, 128, 0.04)',
              }}
            >
              <div className="feature-icon" style={{ color: '#4ade80' }}>
                <Shield size={32} />
              </div>
              <h3 className="feature-title">Our Sponsor-Bank Model</h3>
              <p className="feature-description">
                We underwrite peptide merchants directly with sponsor banks such as{' '}
                <strong style={{ color: '#e4e6eb' }}>Santander</strong>. Your customers pay with
                <strong style={{ color: '#e4e6eb' }}> credit/debit cards</strong> on the sponsor-bank
                rail — and we settle your merchant payouts directly in
                <strong style={{ color: '#e4e6eb' }}> USDT</strong> to your wallet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        id="peptide-how"
        className="platform-section"
        data-testid="peptide-how-section"
      >
        <div className="platform-container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">From application to first crypto payout in ~72 hours.</p>

          <div
            className="platform-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '24px',
              marginTop: '40px',
            }}
          >
            {[
              {
                num: '01',
                title: 'Apply',
                text: 'Submit KYC, business docs, product list, and your USDT wallet address. Our compliance team reviews peptide merchants in under 24 hours.',
              },
              {
                num: '02',
                title: 'Sponsor Bank Underwriting',
                text: 'We sponsor your merchant ID through Santander (or a comparable partner bank) for credit/debit card acceptance — tailored to your peptide-vertical risk profile.',
              },
              {
                num: '03',
                title: 'Accept Card Payments',
                text: 'Your customers check out with standard Visa, Mastercard, Amex, and Discover cards. The processing rail looks like any normal e-commerce checkout to your shopper.',
              },
              {
                num: '04',
                title: 'Get Paid in USDT',
                text: 'We convert your settled USD volume to USDT and send it directly to the wallet address on file. No bank account needed for payouts.',
              },
            ].map((s, i) => (
              <div
                key={s.num}
                className="platform-card"
                data-testid={`peptide-step-${i + 1}`}
              >
                <div
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: '#4ade80',
                    opacity: 0.4,
                    marginBottom: '8px',
                  }}
                >
                  {s.num}
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Reassurance */}
      <section
        className="features-section"
        data-testid="peptide-compliance-section"
        style={{ paddingTop: '40px' }}
      >
        <div className="container">
          <h2 className="section-title">Compliance-First, Not Compliance-Adjacent</h2>
          <p className="section-subtitle">
            The reason banks keep our doors open is the same reason yours stay open.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '20px',
              marginTop: '40px',
            }}
          >
            {[
              {
                icon: <FileCheck size={28} />,
                title: 'Full KYC + KYB',
                text: 'Identity, business formation docs, UBO verification, and ongoing AML monitoring on every transaction.',
              },
              {
                icon: <Landmark size={28} />,
                title: 'Sponsor Bank Partners',
                text: 'Santander-backed merchant relationships with transparent underwriting and clear acceptable-use policies.',
              },
              {
                icon: <Shield size={28} />,
                title: 'AML Transaction Monitoring',
                text: 'Real-time screening against OFAC, SDN, and sanctions lists — built into the payment rail.',
              },
              {
                icon: <Lock size={28} />,
                title: 'SOC 2-Aligned Controls',
                text: 'Encrypted KYC data, audit-logged admin access, and SSO-ready compliance workflows.',
              },
            ].map((c, i) => (
              <div
                key={c.title}
                className="feature-card"
                data-testid={`peptide-compliance-card-${i + 1}`}
              >
                <div className="feature-icon">{c.icon}</div>
                <h3 className="feature-title">{c.title}</h3>
                <p className="feature-description">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <section
        id="peptide-inquiry"
        className="inquiry-section"
        data-testid="peptide-inquiry-section"
      >
        <div className="inquiry-container">
          <h2 className="section-title">Apply for a Peptide Merchant Account</h2>
          <p className="section-subtitle">
            Tell us about your business. A peptide-merchant specialist — not a chatbot — will
            respond within 24 hours.
          </p>

          <div className="inquiry-content">
            <div className="inquiry-info">
              <h3>What We'll Need</h3>
              <ul className="inquiry-benefits">
                <li><CheckCircle size={20} /> Business entity docs (LLC / Corp)</li>
                <li><CheckCircle size={20} /> Beneficial owner ID verification</li>
                <li><CheckCircle size={20} /> Product catalog & supplier info</li>
                <li><CheckCircle size={20} /> USDT wallet address for payouts</li>
                <li><CheckCircle size={20} /> Rough monthly card-processing volume</li>
                <li><CheckCircle size={20} /> Website & terms of service</li>
              </ul>
              <div
                style={{
                  marginTop: '28px',
                  padding: '16px',
                  background: 'rgba(74, 222, 128, 0.08)',
                  border: '1px solid rgba(74, 222, 128, 0.25)',
                  borderRadius: '12px',
                  color: '#9ca3af',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}
                data-testid="peptide-sponsor-bank-note"
              >
                <strong style={{ color: '#4ade80' }}>How it flows:</strong> your customer pays with a
                credit or debit card → cleared by our sponsor bank{' '}
                <strong style={{ color: '#e4e6eb' }}>Santander</strong> → we settle your payout in
                <strong style={{ color: '#e4e6eb' }}> USDT</strong> to your wallet on file.
              </div>
            </div>

            <form
              className="inquiry-form"
              onSubmit={handleSubmit}
              data-testid="peptide-inquiry-form"
            >
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={inquiryForm.name}
                  onChange={handleChange}
                  required
                  placeholder="Jane Chen"
                  data-testid="peptide-input-name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="businessName">Business Name *</label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={inquiryForm.businessName}
                  onChange={handleChange}
                  required
                  placeholder="Acme Peptides LLC"
                  data-testid="peptide-input-business"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={inquiryForm.email}
                  onChange={handleChange}
                  required
                  placeholder="jane@acmepeptides.com"
                  data-testid="peptide-input-email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={inquiryForm.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  data-testid="peptide-input-phone"
                />
              </div>

              <div className="form-group">
                <label htmlFor="monthlyVolume">Expected Monthly Volume *</label>
                <select
                  id="monthlyVolume"
                  name="monthlyVolume"
                  value={inquiryForm.monthlyVolume}
                  onChange={handleChange}
                  required
                  data-testid="peptide-input-volume"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: '#e4e6eb',
                    fontSize: '0.95rem',
                  }}
                >
                  <option value="">Select a range</option>
                  <option value="<$10k">Under $10k / month</option>
                  <option value="$10k-$50k">$10k – $50k / month</option>
                  <option value="$50k-$250k">$50k – $250k / month</option>
                  <option value="$250k-$1M">$250k – $1M / month</option>
                  <option value=">$1M">Over $1M / month</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="usdtWallet">USDT Wallet Address (optional)</label>
                <input
                  type="text"
                  id="usdtWallet"
                  name="usdtWallet"
                  value={inquiryForm.usdtWallet}
                  onChange={handleChange}
                  placeholder="0x... or Tron T... — we'll confirm the chain during onboarding"
                  data-testid="peptide-input-usdt-wallet"
                  autoComplete="off"
                  spellCheck="false"
                />
                <p style={{ color: '#6b7280', fontSize: '0.78rem', margin: '6px 0 0 0' }}>
                  Where we'll send your settled merchant payouts. You can add this during onboarding instead.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="message">Anything else we should know?</label>
                <textarea
                  id="message"
                  name="message"
                  value={inquiryForm.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Previous processor history, product mix, target markets, etc."
                  data-testid="peptide-input-message"
                />
              </div>

              {status.message && (
                <div
                  className={`inquiry-status ${status.type}`}
                  data-testid="peptide-inquiry-status"
                >
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={submitting}
                data-testid="peptide-inquiry-submit"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
                <ArrowRight size={20} />
              </button>

              <p className="inquiry-note">
                Your information is kept confidential and routed directly to our underwriting team.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section" data-testid="peptide-faq-section">
        <div className="faq-container">
          <h2 className="section-title">Peptide Merchant FAQ</h2>
          <p className="section-subtitle">Answers to what peptide operators actually ask us.</p>

          <div className="faq-accordion">
            <PeptideFAQItem
              testId="peptide-faq-1"
              question="How do my customers actually pay?"
              answer="Standard credit and debit cards — Visa, Mastercard, Amex, Discover. The checkout looks like any normal e-commerce flow to your shopper. The cards are processed through our sponsor bank (Santander) on a merchant ID specifically underwritten for peptide businesses."
            />
            <PeptideFAQItem
              testId="peptide-faq-2"
              question="How do I get paid?"
              answer="We settle merchant payouts directly in USDT (Tether) to a wallet address you provide during onboarding. You don't need a US bank account on the payout side. Most chains supported (ERC-20, TRC-20, Solana, Polygon)."
            />
            <PeptideFAQItem
              testId="peptide-faq-3"
              question="What sponsor banks do you work with?"
              answer="Our primary peptide-vertical relationship is with Santander. Depending on your risk profile and transaction volume we may also route merchants through secondary sponsor banks. Final assignment happens during underwriting."
            />
            <PeptideFAQItem
              testId="peptide-faq-4"
              question="Can my customers pay in crypto instead?"
              answer="Yes. While our flagship peptide flow is card-in / USDT-out, we can also enable crypto checkout (BTC, ETH, USDT, USDC, and 300+ others) as a secondary payment option for customers who prefer it. Ask during underwriting."
            />
            <PeptideFAQItem
              testId="peptide-faq-5"
              question="What about chargebacks?"
              answer="Card payments do carry chargeback risk — that's the trade-off for accepting cards. Our sponsor-bank relationship is structured specifically for peptide merchants: realistic chargeback thresholds, dispute representment support, and we won't freeze your account at the first whiff of a complaint like generic processors do. If you prefer zero chargebacks, ask about our crypto checkout option."
            />
            <PeptideFAQItem
              testId="peptide-faq-6"
              question="What does it cost?"
              answer="Card processing fees are 4–6% (sponsor bank pricing for high-risk verticals), plus our platform fee. USDT payouts are free of bank wire fees. Enterprise merchants (>$250k/mo) qualify for custom pricing — talk to us during application."
            />
            <PeptideFAQItem
              testId="peptide-faq-7"
              question="How quickly can I start accepting payments?"
              answer="Most peptide merchants are live within 48–72 hours of submitting a complete application. Incomplete KYC, missing business docs, or a missing USDT wallet address are the main reasons for delays."
            />
            <PeptideFAQItem
              testId="peptide-faq-8"
              question="Am I on the MATCH list? Can you still approve me?"
              answer="Possibly. We review MATCH-listed merchants case-by-case. The sponsor-bank underwriting model gives us flexibility traditional processors don't have — but honesty during the application is non-negotiable."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="peptide-cta-section">
        <h2>Stop Getting Shut Down.</h2>
        <p>
          Apply once. Get underwritten by a sponsor bank that actually understands your vertical.
        </p>
        <div className="cta-buttons">
          <button
            className="btn btn-primary btn-large"
            onClick={scrollToInquiry}
            data-testid="peptide-final-cta"
          >
            Apply Now <ArrowRight size={20} />
          </button>
          <button
            className="btn btn-secondary btn-large"
            onClick={() => navigate('/')}
            data-testid="peptide-back-to-main"
          >
            Back to Main Site
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="peptide-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <Bitcoin size={24} />
            <span>Crypto Cashier</span>
          </div>
          <p className="footer-text">© 2026 Crypto Cashier. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PeptideLanding;
